import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import PrivateRoute from "./components/common/PrivateRoute";
import PopupBanner from "./components/common/PopupBanner";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import { FullPageSpinner } from "./components/common/LoadingSpinner";

// Public Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Academics from "./pages/Academics";
import Admissions from "./pages/Admissions";
import AdmissionForm from "./pages/AdmissionForm";
import StudentLife from "./pages/StudentLife";
import NewsEvents from "./pages/NewsEvents";
import Gallery from "./pages/Gallery";
import Notices from "./pages/Notices";
import Results from "./pages/Results";
import Contact from "./pages/Contact";
import Login from "./pages/Login";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminAdmissions from "./pages/admin/AdminAdmissions";
import AdminNotices from "./pages/admin/AdminNotices";
import AdminEvents from "./pages/admin/AdminEvents";
import AdminGallery from "./pages/admin/AdminGallery";
import AdminResults from "./pages/admin/AdminResults";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminBuses from "./pages/admin/AdminBuses";
import AdminStore from "./pages/admin/AdminStore";
import AdminStaffAttendance from "./pages/admin/AdminStaffAttendance";
import AdminInfrastructure from "./pages/admin/AdminInfrastructure";
import AdminStaffAccounts from "./pages/admin/AdminStaffAccounts";
import AdminLoginHistory from "./pages/admin/AdminLoginHistory";

// Roles allowed on the website-content modules
const WEB_ROLES = ["admin", "web_management"];
// Roles allowed on the operational modules (bus/store/infrastructure/staff attendance)
const STORE_ROLES = ["admin", "store_management"];

// Layout wrapper that shows/hides Navbar and Footer based on route
const PublicLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isLoginRoute = location.pathname === "/login";

  if (isAdminRoute || isLoginRoute) {
    return <>{children}</>;
  }

  return (
    <>
      <PopupBanner />
      <Navbar />
      <main className="pt-20 sm:pt-24">{children}</main>
      <Footer />
    </>
  );
};

const AppRoutes = () => (
  <PublicLayout>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/academics" element={<Academics />} />
      <Route path="/admissions" element={<Admissions />} />
      <Route path="/admissions/apply" element={<AdmissionForm />} />
      <Route path="/student-life" element={<StudentLife />} />
      <Route path="/news-events" element={<NewsEvents />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/notices" element={<Notices />} />
      <Route path="/results" element={<Results />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/login" element={<Login />} />

      {/* Admin Routes (Protected) */}
      <Route
        path="/admin"
        element={
          <PrivateRoute roles={["admin"]}>
            <AdminDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/admissions"
        element={
          <PrivateRoute roles={WEB_ROLES}>
            <AdminAdmissions />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/notices"
        element={
          <PrivateRoute roles={WEB_ROLES}>
            <AdminNotices />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/events"
        element={
          <PrivateRoute roles={WEB_ROLES}>
            <AdminEvents />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/gallery"
        element={
          <PrivateRoute roles={WEB_ROLES}>
            <AdminGallery />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/results"
        element={
          <PrivateRoute roles={WEB_ROLES}>
            <AdminResults />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/contacts"
        element={
          <PrivateRoute roles={WEB_ROLES}>
            <AdminContacts />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <PrivateRoute roles={["admin"]}>
            <AdminSettings />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/staff-accounts"
        element={
          <PrivateRoute roles={["admin"]}>
            <AdminStaffAccounts />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/login-history"
        element={
          <PrivateRoute roles={["admin"]}>
            <AdminLoginHistory />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/buses"
        element={
          <PrivateRoute roles={STORE_ROLES}>
            <AdminBuses />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/store"
        element={
          <PrivateRoute roles={STORE_ROLES}>
            <AdminStore />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/staff-attendance"
        element={
          <PrivateRoute roles={STORE_ROLES}>
            <AdminStaffAttendance />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/infrastructure"
        element={
          <PrivateRoute roles={STORE_ROLES}>
            <AdminInfrastructure />
          </PrivateRoute>
        }
      />

      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center px-4">
              <div className="text-8xl font-bold text-primary-100 mb-4">
                404
              </div>
              <h1 className="text-3xl font-serif font-bold text-primary-500 mb-3">
                Page Not Found
              </h1>
              <p className="text-gray-500 mb-6">
                The page you are looking for does not exist or has been moved.
              </p>
              <a
                href="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors"
              >
                Back to Home
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  </PublicLayout>
);

const App = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <SiteSettingsProvider>
          <Suspense fallback={<FullPageSpinner />}>
            <AppRoutes />
          </Suspense>
        </SiteSettingsProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
